/**
 * Client for the agents-core SSE chat endpoint.
 *
 * The orchestrator streams Server-Sent Events with `event:` and `data:`
 * lines; we POST + parse the response body manually because EventSource
 * doesn't support custom request bodies.
 */

export type AgenticEvent =
  | { type: 'session'; data: { session_uuid: string } }
  | { type: 'turn_start'; data: { user_id: number; session: string } }
  | { type: 'memory_hits'; data: { count: number } }
  | { type: 'agent_selected'; data: { agent: string; reasoning: string } }
  | { type: 'tool_call'; data: { agent: string; tool: string; arguments: unknown } }
  | {
      type: 'tool_result';
      data: {
        agent: string;
        tool: string;
        ok: boolean;
        content: string;
        artifacts: Array<{ type: 'image' | 'map'; url: string; [k: string]: unknown }>;
      };
    }
  | { type: 'agent_text'; data: { agent: string; text: string } }
  | { type: 'orchestrator_text'; data: { text: string } }
  | { type: 'turn_done'; data: { agent: string | null; summary: string } }
  | { type: 'error'; data: { message: string } };

export interface UserProfile {
  user_id: number;
  username: string;
  display_name: string;
  profile: { name: string; description: string };
  welcome_message: string;
  datasets: Array<{ key: string; display_name: string; description: string }>;
  agents: Array<{ key: string; display_name: string; description: string }>;
}

// In dev, API_AGENT_CORE is unset so we fall back to the proxy path.
// In prod (.env.production), API_AGENT_CORE is the same proxy path served by nginx.
const _root: string = (import.meta.env.API_AGENT_CORE as string | undefined) ?? '/api/agent-core';

// API base — strip trailing slash, append /api (the agents-core mount point)
const BASE: string = `${_root.replace(/\/$/, '')}/api`;

// Root origin — strip trailing /api or /api/ for endpoints like /health
export const agenticRootUrl: string = BASE.replace(/\/api\/?$/, '');

/** @deprecated use agenticRootUrl for non-api endpoints */
export const agenticBaseUrl = agenticRootUrl;

export async function getMe(username: string): Promise<UserProfile> {
  const res = await fetch(`${BASE}/me?username=${encodeURIComponent(username)}`);
  if (!res.ok) throw new Error(`getMe failed: ${res.status}`);
  return res.json();
}

export async function newSession(username: string): Promise<string> {
  const res = await fetch(
    `${BASE}/session?username=${encodeURIComponent(username)}`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(`newSession failed: ${res.status}`);
  const j = await res.json();
  return j.session_uuid;
}

export async function* streamChat(opts: {
  username: string;
  message: string;
  sessionUuid?: string;
  signal?: AbortSignal;
}): AsyncGenerator<AgenticEvent> {
  const res = await fetch(`${BASE}/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({
      username: opts.username,
      message: opts.message,
      session_uuid: opts.sessionUuid,
    }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) throw new Error(`chat/stream failed: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventType = 'message';
      const dataLines: string[] = [];
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length === 0) continue;
      try {
        const data = JSON.parse(dataLines.join('\n'));
        yield { type: eventType, data } as AgenticEvent;
      } catch {
        // malformed event, skip
      }
    }
  }
}

/** Resolve a server-relative artifact URL to an absolute URL. */
export function artifactUrl(rel: string): string {
  if (!rel) return rel;
  if (rel.startsWith('http')) return rel;
  return `${BASE}${rel}`;
}
